// The offboarding position on a driver record. Three things can show here: the
// live case with a link to it, an "Offboard this driver" action for a working
// driver, and, for a returning applicant, how their last case ended and whether
// they may be rehired. Renders nothing when none of those apply, so a pipeline
// applicant with no history sees no empty box.
//
// Opening a case here is the by-hand route. The status change route is behind a
// switch (driver_offboarding_settings.auto_open_on_status_change) that is off
// until Luyanda turns it on, so today this button is how every case starts.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { DoorOpen, ExternalLink, UserMinus } from 'lucide-react'
import { useGetDriverOffboarding, useOpenOffboarding, useSignOffboardingUpload } from '../../hooks/backend/offboarding'
import { DOCUMENT_ACCEPT, checkDocument, uploadSigned, type Signed } from '../../utils/signedUpload'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import {
  EXIT_TYPES, STATUS_LABEL, STATUS_TONE, exitTypeLabel, formatDay, isDismissal, payDueText, progress,
  type DriverOffboarding,
} from '../../utils/offboarding'

export function DriverOffboardingPanel({ applicationId, isActiveDriver }: { applicationId: string; isActiveDriver: boolean }) {
  const q = useGetDriverOffboarding<DriverOffboarding>()
  const load = q.trigger
  const [opening, setOpening] = useState(false)

  const refresh = useCallback(() => {
    void load({ application_id: applicationId }, { skipCache: true }).catch(() => undefined)
  }, [load, applicationId])
  useEffect(() => { refresh() }, [refresh])

  const d = q.data
  if (!d || !d.access?.can_view) return null
  const live = d.live_case ?? null
  const past = d.past_cases ?? []
  const canOpen = d.access.can_open && isActiveDriver && !live && !!d.driver_id
  if (!live && past.length === 0 && !canOpen) return null

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <DoorOpen className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Offboarding</h3>
        {canOpen && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpening(true)}>
            <UserMinus className="mr-1 h-4 w-4" /> Offboard this driver
          </Button>
        )}
      </div>

      {live && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{live.case_ref}</span>
            <Chip tone={STATUS_TONE[live.status]}>{STATUS_LABEL[live.status]}</Chip>
            <Chip tone={payDueText(live).tone}>{payDueText(live).text}</Chip>
            {live.items_overdue > 0 && <Chip tone="red">{live.items_overdue} overdue</Chip>}
          </div>
          <p className="text-xs text-muted-foreground">
            {exitTypeLabel(live.exit_type)}. Last working day {formatDay(live.last_working_day)}
            {live.dates_confirmed ? '' : ' (provisional)'}. {progress(live).settled} of {progress(live).total} steps settled.
          </p>
          <Link to={`/offboarding?case=${live.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--brand))] hover:underline">
            Open the case <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      {!live && d.rehire && (
        <div className="mt-3 space-y-1 text-sm">
          <p className="text-foreground">
            Left Fleetly on {formatDay(d.rehire.employment_end_date)} ({exitTypeLabel(d.rehire.exit_type)}, {d.rehire.case_ref}).
          </p>
          <p className="text-xs">
            <span className="font-medium text-foreground">Rehire: </span>
            {d.rehire.rehire_eligible === 'yes' ? <span className="text-emerald-700 dark:text-emerald-300">Eligible</span>
              : d.rehire.rehire_eligible === 'no' ? <span className="text-rose-700 dark:text-rose-300">Not eligible</span>
              : d.rehire.rehire_eligible === 'review' ? <span className="text-amber-700 dark:text-amber-300">To be reviewed</span>
              : <span className="text-muted-foreground">Not recorded</span>}
            {d.rehire.rehire_reason ? `. ${d.rehire.rehire_reason}` : ''}
          </p>
        </div>
      )}

      {!live && !d.rehire && canOpen && (
        <p className="mt-2 text-xs text-muted-foreground">
          When this driver leaves for any reason, open the case here. It raises the dated checklist, the final pay schedule and the exit interview.
        </p>
      )}

      {canOpen && d.driver_id && (
        <OpenCaseDialog open={opening} onOpenChange={setOpening} driverId={d.driver_id} onOpened={refresh} />
      )}
    </section>
  )
}

// THE SOURCE DOCUMENT IS A FILE (Luyanda, 2 October 2026; migration 2400). A
// resignation needs the resignation itself, and a dismissal the official
// hearing finding: a WhatsApp or email screenshot, a photo of the letter, or a
// PDF. The file goes up first through a one-shot link, then the case is opened
// with its path, and Postgres checks the file is really in storage. The other
// exit types take a file without requiring one.
function OpenCaseDialog({ open, onOpenChange, driverId, onOpened }: {
  open: boolean; onOpenChange: (v: boolean) => void; driverId: string; onOpened: () => void
}) {
  const openCase = useOpenOffboarding<{ ok: boolean; case_id: string; already_open?: boolean }>()
  const sign = useSignOffboardingUpload<Signed>()
  const [exitType, setExitType] = useState('')
  const [noticeDate, setNoticeDate] = useState('')
  const [lastDay, setLastDay] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const dismissal = isDismissal(exitType)
  const needsFile = exitType === 'resignation' || dismissal
  const fileProblem = file ? checkDocument(file) : null

  async function submit() {
    setBusy(true)
    try {
      let path: string | null = null
      if (file) path = await uploadSigned(await sign.trigger({ driver_id: driverId, filename: file.name }, { skipCache: true }), file)
      const r = await openCase.trigger({
        driver_id: driverId, exit_type: exitType || null, notice_date: noticeDate || null,
        last_working_day: lastDay || null, document_path: path, document_filename: file?.name ?? null,
      }).result
      toast.success(r?.already_open ? 'A case is already open for this driver.' : 'Offboarding case opened.')
      setFile(null)
      onOpenChange(false)
      onOpened()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Offboard this driver</DialogTitle>
          <DialogDescription>
            Opens one case with the dated checklist against named people. Nothing is sent to the employee until someone sends the letter from the case.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ob-exit-type">Exit type</Label>
            <NativeSelect id="ob-exit-type" value={exitType} onChange={(e) => setExitType(e.target.value)}>
              <option value="">Choose one</option>
              {EXIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </NativeSelect>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ob-notice">Notice received or given</Label>
              <Input id="ob-notice" type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ob-last">Last working day</Label>
              <Input id="ob-last" type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Leave the last working day empty if it is not certain. The case opens on a provisional date and every step is redated when it is confirmed.
          </p>
          <div className="space-y-1">
            <Label htmlFor="ob-file">
              {exitType === 'resignation' ? 'The resignation' : dismissal ? 'The official hearing finding' : 'Source document'}
              {needsFile ? '' : ' (optional)'}
            </Label>
            <Input id="ob-file" type="file" accept={DOCUMENT_ACCEPT}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <p className="text-xs text-muted-foreground">
              {exitType === 'resignation'
                ? 'A screenshot of the WhatsApp message or email, or a photo or PDF of the letter. It becomes the source document of the case.'
                : dismissal
                  ? 'A photo, screenshot or PDF of the signed outcome. A dismissal case cannot be opened without it. The case records that fair procedure happened; it does not decide the dismissal.'
                  : 'A photo, screenshot or PDF. You can add it later from the case.'}
            </p>
            {fileProblem && <p className="text-xs text-rose-700 dark:text-rose-300">{fileProblem}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="brand" onClick={() => void submit()}
            disabled={!exitType || busy || (needsFile && !file) || !!fileProblem}>
            {busy && <Spinner className="mr-1 h-4 w-4" />} {file ? 'Upload and open the case' : 'Open the case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
